// App.tsx - Updated with MobileHeader
import React, { useState, useEffect, useRef } from 'react';
import 'leaflet/dist/leaflet.css';
import { Point, DestinationForm, TravelProfile } from './types';
import { calculateDistance, solveTSP } from './utils/mapUtils';
import { getDrivingDirections, searchPlace } from './utils/apiUtils';
import { MobileHeader } from './components/MobileHeader';
import { Sidebar } from './components/Sidebar';
import { SearchBar } from './components/SearchBar';
import { DestinationModal } from './components/DestinationModal';
import { DirectionsPanel } from './components/DirectionsPanel';
import { MapComponent } from './components/MapComponent';
import { InfoOverlay } from './components/InfoOverlay';

// Sample points data
const SAMPLE_POINTS: Point[] = [
  { id: 1, lat: 0.7160742, lng: 73.4386991, name: 'Sosunvilla', contact: '+960 123-4567', visited: false },
  { id: 2, lat: 0.7163357, lng: 73.4374800, name: 'Artificial Beach', contact: '+960 234-5678', visited: false },
  { id: 3, lat: 0.7187850, lng: 73.4382086, name: 'Malé Fish Market', contact: '+960 345-6789', visited: false },
];

function App() {
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [points, setPoints] = useState<Point[]>(SAMPLE_POINTS);
  const [route, setRoute] = useState<number[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapKey, setMapKey] = useState(0);
  const [travelProfile, setTravelProfile] = useState<TravelProfile>('driving-car');
  const [directions, setDirections] = useState<any>(null);
  const [isLoadingDirections, setIsLoadingDirections] = useState(false);
  const [routeGeometry, setRouteGeometry] = useState<Array<[number, number]>>([]);
  const [showDirectionsPanel, setShowDirectionsPanel] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Add destination form state
  const [showAddDestination, setShowAddDestination] = useState(false);
  const [newDestination, setNewDestination] = useState<DestinationForm>({
    name: '',
    contact: '',
    lat: 0,
    lng: 0,
  });
  const [isPickingLocation, setIsPickingLocation] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [visitedPoints, setVisitedPoints] = useState<Set<number>>(new Set());
  const [flyToLocation, setFlyToLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get user location
  useEffect(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setIsLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation([position.coords.latitude, position.coords.longitude]);
        setIsLoading(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        setUserLocation([0.7160742, 73.4386991]);
        setError('Using default location. Please allow location access for better experience.');
        setIsLoading(false);
      },
      { enableHighAccuracy: true }
    );
  }, []);

  // Calculate fastest route when user location or points change
  useEffect(() => {
    if (userLocation && points.length > 0) {
      const allPoints = [
        { lat: userLocation[0], lng: userLocation[1], name: 'My Location' },
        ...points
      ];
      const routePath = solveTSP(allPoints, 0);
      setRoute(routePath);
    } else {
      setRoute(null);
    }
  }, [userLocation, points]);

  // Fetch driving directions when route changes
  useEffect(() => {
    const fetchDirections = async () => {
      if (!route || !userLocation || points.length === 0) {
        setDirections(null);
        setRouteGeometry([]);
        return;
      }
      
      setIsLoadingDirections(true);
      const allPoints = [
        { lat: userLocation[0], lng: userLocation[1], name: 'My Location' },
        ...points
      ];
      
      const waypoints = route.map(idx => ({
        lat: allPoints[idx].lat,
        lng: allPoints[idx].lng
      }));
      
      const data = await getDrivingDirections(waypoints, travelProfile);
      
      if (data && data.features && data.features.length > 0) {
        const feature = data.features[0];
        setDirections(data);
        
        if (feature.geometry && feature.geometry.coordinates) {
          const coords = feature.geometry.coordinates.map((c: [number, number]) => [c[1], c[0]] as [number, number]);
          setRouteGeometry(coords);
        } else if (feature.geometry && feature.geometry.points) {
          const coords = feature.geometry.points.map((c: [number, number]) => [c[1], c[0]] as [number, number]);
          setRouteGeometry(coords);
        }
      } else {
        const coords = route.map(idx => [allPoints[idx].lat, allPoints[idx].lng] as [number, number]);
        setRouteGeometry(coords);
        setError('Using straight-line routing. API may be rate-limited.');
      }
      
      setIsLoadingDirections(false);
    };

    fetchDirections();
  }, [route, travelProfile]);

  // Handle search with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      const results = await searchPlace(searchQuery);
      setSearchResults(results);
      setShowSearchResults(true);
      setIsSearching(false);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Toggle visited status
  const toggleVisited = (id: number) => {
    const newVisited = new Set(visitedPoints);
    if (newVisited.has(id)) {
      newVisited.delete(id);
    } else {
      newVisited.add(id);
    }
    setVisitedPoints(newVisited);
    
    setPoints(points.map(p => 
      p.id === id ? { ...p, visited: !p.visited } : p
    ));
  };

  // Center map on a location
  const centerOnLocation = (lat: number, lng: number) => {
    setFlyToLocation({ lat, lng });
    setTimeout(() => {
      setFlyToLocation(null);
    }, 2000);
  };

  // Add a searched place as a point
  const addSearchedPlace = (place: any) => {
    const lat = parseFloat(place.lat);
    const lng = parseFloat(place.lon);
    
    let name = '';
    if (place.address) {
      name = place.address.house_number 
        ? `${place.address.house_number} ${place.address.road || place.address.street || ''}`
        : place.address.road || place.address.street || '';
      
      if (!name) {
        name = place.address.neighbourhood || 
               place.address.suburb || 
               place.address.city || 
               place.address.town || 
               place.address.village || 
               place.address.county ||
               place.display_name.split(',')[0];
      }
    }
    
    if (!name) {
      name = place.display_name?.split(',')[0] || 'Searched Place';
    }
    
    const newPoint: Point = {
      id: Date.now(),
      lat,
      lng,
      name: name.trim(),
      contact: '',
      visited: false
    };
    
    setPoints([...points, newPoint]);
    setShowSearchResults(false);
    setSearchQuery('');
    setSearchResults([]);
    setTimeout(() => centerOnLocation(lat, lng), 100);
  };

  // Handle search result selection
  const handleSearchResultSelect = (result: any) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    if (isPickingLocation) {
      setNewDestination({ ...newDestination, lat, lng });
      setIsPickingLocation(false);
      setError(null);
      centerOnLocation(lat, lng);
    } else {
      addSearchedPlace(result);
    }
  };

  // Add new point at clicked location
  const handleMapClick = (e: L.LeafletMouseEvent) => {
    if (isPickingLocation) {
      const { lat, lng } = e.latlng;
      setNewDestination({
        ...newDestination,
        lat,
        lng
      });
      setIsPickingLocation(false);
      setError(null);
      centerOnLocation(lat, lng);
    }
  };

  // Handle form submission for new destination
  const handleSubmitDestination = () => {
    if (!newDestination.name.trim()) {
      setError('Please enter a destination name');
      return;
    }
    if (!newDestination.contact.trim()) {
      setError('Please enter contact information');
      return;
    }
    if (newDestination.lat === 0 && newDestination.lng === 0) {
      setError('Please select a location on the map');
      return;
    }

    if (editingId !== null) {
      const updatedPoints = points.map(p => 
        p.id === editingId 
          ? { ...p, name: newDestination.name, contact: newDestination.contact, lat: newDestination.lat, lng: newDestination.lng }
          : p
      );
      setPoints(updatedPoints);
      setEditingId(null);
    } else {
      const newPoint: Point = {
        id: Date.now(),
        lat: newDestination.lat,
        lng: newDestination.lng,
        name: newDestination.name,
        contact: newDestination.contact,
        visited: false
      };
      setPoints([...points, newPoint]);
    }

    setNewDestination({ name: '', contact: '', lat: 0, lng: 0 });
    setShowAddDestination(false);
    setError(null);
    setRoute(null);
    setDirections(null);
    setRouteGeometry([]);
    setTimeout(() => centerOnLocation(newDestination.lat, newDestination.lng), 100);
  };

  // Edit a point
  const editPoint = (point: Point) => {
    setNewDestination({
      name: point.name,
      contact: point.contact || '',
      lat: point.lat,
      lng: point.lng
    });
    setEditingId(point.id);
    setShowAddDestination(true);
    centerOnLocation(point.lat, point.lng);
  };

  // Remove a point
  const removePoint = (id: number) => {
    const updatedPoints = points.filter(p => p.id !== id);
    setPoints(updatedPoints);
    setVisitedPoints(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
    setRoute(null);
    setDirections(null);
    setRouteGeometry([]);
  };

  // Reset points to sample data
  const resetPoints = () => {
    setPoints(SAMPLE_POINTS);
    setVisitedPoints(new Set());
    setDirections(null);
    setRouteGeometry([]);
    setRoute(null);
  };

  // Get total distance
  const getTotalDistance = (): number => {
    if (!route || !userLocation || points.length === 0) return 0;
    
    if (directions && directions.features && directions.features[0]) {
      const dist = directions.features[0].properties.summary.distance;
      return dist / 1000;
    }
    
    const allPoints = [
      { lat: userLocation[0], lng: userLocation[1], name: 'My Location' },
      ...points
    ];
    
    let total = 0;
    for (let i = 0; i < route.length - 1; i++) {
      total += calculateDistance(
        allPoints[route[i]].lat, allPoints[route[i]].lng,
        allPoints[route[i + 1]].lat, allPoints[route[i + 1]].lng
      );
    }
    return total;
  };

  // Get estimated duration
  const getDuration = (): string => {
    if (directions && directions.features && directions.features[0]) {
      const duration = directions.features[0].properties.summary.duration;
      const hours = Math.floor(duration / 3600);
      const minutes = Math.floor((duration % 3600) / 60);
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes}m`;
    }
    return 'N/A';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Getting your location...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <MobileHeader
        onAddDestination={() => {
          setNewDestination({ name: '', contact: '', lat: 0, lng: 0 });
          setEditingId(null);
          setShowAddDestination(true);
        }}
        onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        isSidebarOpen={isSidebarOpen}
      />

      <div className="flex" style={{ height: 'calc(100vh - 52px)' }}>
        <Sidebar
          isOpen={isSidebarOpen}
          points={points}
          route={route}
          visitedPoints={visitedPoints}
          travelProfile={travelProfile}
          totalDistance={getTotalDistance()}
          duration={getDuration()}
          userLocation={userLocation}
          onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
          onToggleVisited={toggleVisited}
          onEditPoint={editPoint}
          onRemovePoint={removePoint}
          onCenterLocation={centerOnLocation}
          onTravelProfileChange={setTravelProfile}
          onAddDestination={() => {
            setNewDestination({ name: '', contact: '', lat: 0, lng: 0 });
            setEditingId(null);
            setShowAddDestination(true);
          }}
          onResetPoints={resetPoints}
          onRefreshMap={() => setMapKey(prev => prev + 1)}
          onToggleDirections={() => setShowDirectionsPanel(!showDirectionsPanel)}
          showDirections={showDirectionsPanel}
          isLoadingDirections={isLoadingDirections}
          error={error}
        />

        <div className="flex-1 relative">
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-[1000] w-full max-w-xs md:max-w-sm px-2">
            <SearchBar
              query={searchQuery}
              results={searchResults}
              isSearching={isSearching}
              showResults={showSearchResults}
              onQueryChange={setSearchQuery}
              onResultSelect={handleSearchResultSelect}
              isPickingLocation={isPickingLocation}
            />
          </div>

          <DestinationModal
            isOpen={showAddDestination}
            editingId={editingId}
            formData={newDestination}
            isPickingLocation={isPickingLocation}
            error={error}
            onClose={() => {
              setShowAddDestination(false);
              setIsPickingLocation(false);
              setEditingId(null);
              setNewDestination({ name: '', contact: '', lat: 0, lng: 0 });
              setError(null);
            }}
            onFormChange={(data) => setNewDestination({ ...newDestination, ...data })}
            onPickLocation={() => setIsPickingLocation(true)}
            onSubmit={handleSubmitDestination}
          />

          {userLocation && (
            <MapComponent
              center={userLocation}
              points={points}
              route={route}
              routeGeometry={routeGeometry}
              visitedPoints={visitedPoints}
              travelProfile={travelProfile}
              onMapClick={handleMapClick}
              onEditPoint={editPoint}
              onRemovePoint={removePoint}
              onToggleVisited={toggleVisited}
              flyToLocation={flyToLocation}
            />
          )}

          {/* Minimal Info Overlay */}
          <div className="absolute bottom-2 left-2 bg-white bg-opacity-90 p-2 rounded-lg shadow-lg z-[1000]">
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium">📍 {points.length} stops</span>
              <span className="text-gray-400">|</span>
              <span className="text-blue-600">{getTotalDistance().toFixed(1)} km</span>
              {isLoadingDirections && (
                <div className="animate-spin inline-block ml-1">⟳</div>
              )}
            </div>
          </div>
        </div>

        <DirectionsPanel
          isOpen={showDirectionsPanel}
          directions={directions}
          totalDistance={getTotalDistance()}
          duration={getDuration()}
          onClose={() => setShowDirectionsPanel(false)}
        />
      </div>
    </div>
  );
}

export default App;